import React, { useState, useEffect } from 'react';
import { customFunctions, agents, phoneNumbers } from '../api';
import { Code, Plus, Loader2, CheckCircle, XCircle, AlertTriangle, Save, X, Edit, Trash2, FlaskConical, Target, Link as LinkIcon, Settings, Zap, ToggleLeft, ToggleRight, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Filter, Copy, Clipboard, Phone, MessageSquare } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import AnimatedDropdown from './AnimatedDropdown';

const CustomFunctions = () => {
  const [functions, setFunctions] = useState([]);
  const [agentsList, setAgentsList] = useState([]);
  const [phoneNumbersList, setPhoneNumbersList] = useState([]);
  const [editingFunction, setEditingFunction] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [expandedPrompts, setExpandedPrompts] = useState({});
  const [agentFilter, setAgentFilter] = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [deleteModal, setDeleteModal] = useState({ open: false, functionId: null, functionName: '' });
  const [copySuccess, setCopySuccess] = useState(false);
  const [pasteError, setPasteError] = useState('');

  const [functionData, setFunctionData] = useState({
    name: '',
    description: '',
    trigger_event: 'call_started',
    target_agent_id: '', // New field for agent targeting
    webhook_url: '',
    http_method: 'POST',
    parameters: '{}',
    system_prompt_modification: '',
    greeting_modification: '',
    is_active: true
  });

  const triggerEvents = [
    { value: 'call_started', label: 'Call Started - Before agent speaks', channel: 'call' },
    { value: 'call_ended', label: 'Call Ended - After call finishes', channel: 'call' },
    { value: 'agent_response', label: 'Agent Response - After agent speaks', channel: 'call' },
    { value: 'agent_tool', label: 'Agent Tool - Agent calls it and waits for the result', channel: 'call' },
    { value: 'call_analyzed', label: 'Call Analyzed - After AI analysis completes', channel: 'call' },
    { value: 'chat_form_submitted', label: 'Chat Form Submitted - When visitor submits pre-chat form', channel: 'chat' },
    { value: 'chat_started', label: 'Chat Started - When chat session begins', channel: 'chat' },
    { value: 'chat_message', label: 'Chat Message - On each user message', channel: 'chat' },
    { value: 'chat_ended', label: 'Chat Ended - When chat session closes', channel: 'chat' },
  ];

  const CHAT_EVENTS = new Set(['chat_form_submitted', 'chat_started', 'chat_message', 'chat_ended']);

  const getChannelForEvent = (triggerEvent) =>
    CHAT_EVENTS.has(triggerEvent) ? 'chat' : 'call';

  useEffect(() => {
    loadFunctions();
    loadAgents();
    loadPhoneNumbers();
  }, []);

  // Separate effect to handle function editing after functions are loaded
  useEffect(() => {
    const editFunctionId = sessionStorage.getItem('editFunctionId');
    if (editFunctionId && functions.length > 0 && !showEditor) {
      sessionStorage.removeItem('editFunctionId');
      const func = functions.find(f => f.id === editFunctionId);
      if (func) {
        setEditingFunction(func);
        const mappedData = {
          name: func.name || '',
          description: func.description || '',
          trigger_event: func.trigger_event || 'call_started',
          target_agent_id: func.target_agent_id || '',
          webhook_url: func.webhook_url || '',
          http_method: func.http_method || 'POST',
          parameters: func.parameters_schema || func.parameters || '{}',
          system_prompt_modification: func.system_prompt_modification || '',
          greeting_modification: func.greeting_modification || '',
          timeout_ms: func.timeout_ms || 5000,
          max_retries: func.max_retries || 2,
          auth_header: func.auth_header || '',
          is_active: func.is_active !== undefined ? func.is_active : true
        };
        setFunctionData(mappedData);
        setShowEditor(true);
      }
    }
  }, [functions, showEditor]);

  const copyFunctionAsJSON = async (func) => {
    try {
      // Create a clean copy of the function data (excluding id and timestamps)
      const functionJSON = {
        name: func.name || '',
        description: func.description || '',
        trigger_event: func.trigger_event || 'call_started',
        target_agent_id: func.target_agent_id || '',
        webhook_url: func.webhook_url || '',
        http_method: func.http_method || 'POST',
        parameters: func.parameters_schema || func.parameters || '{}',
        system_prompt_modification: func.system_prompt_modification || '',
        greeting_modification: func.greeting_modification || '',
        timeout_ms: func.timeout_ms || 5000,
        max_retries: func.max_retries || 2,
        auth_header: func.auth_header || '',
        is_active: func.is_active !== undefined ? func.is_active : true
      };

      const jsonString = JSON.stringify(functionJSON, null, 2);
      await navigator.clipboard.writeText(jsonString);
      
      setCopySuccess(true);
      setMessage(`Function "${func.name}" copied as JSON to clipboard!`);
      setTimeout(() => {
        setCopySuccess(false);
        setMessage('');
      }, 3000);
    } catch (err) {
      setError('Failed to copy function: ' + err.message);
    }
  };

  const pasteFunctionFromJSON = async () => {
    try {
      setPasteError('');
      setError('');
      
      // Read from clipboard
      const clipboardText = await navigator.clipboard.readText();
      
      // Parse JSON
      let parsedData;
      try {
        parsedData = JSON.parse(clipboardText);
      } catch (e) {
        setPasteError('Invalid JSON format. Please check your clipboard content.');
        return;
      }

      // Validate required fields
      if (!parsedData.name || !parsedData.webhook_url) {
        setPasteError('JSON must include "name" and "webhook_url" fields.');
        return;
      }

      // Map parsed data to form fields
      const mappedData = {
        name: parsedData.name || '',
        description: parsedData.description || '',
        trigger_event: parsedData.trigger_event || 'call_started',
        target_agent_id: parsedData.target_agent_id || '',
        webhook_url: parsedData.webhook_url || '',
        http_method: parsedData.http_method || 'POST',
        parameters: typeof parsedData.parameters === 'string' 
          ? parsedData.parameters 
          : JSON.stringify(parsedData.parameters || {}, null, 2),
        system_prompt_modification: parsedData.system_prompt_modification || '',
        greeting_modification: parsedData.greeting_modification || '',
        timeout_ms: parsedData.timeout_ms || 5000,
        max_retries: parsedData.max_retries || 2,
        auth_header: parsedData.auth_header || '',
        is_active: parsedData.is_active !== undefined ? parsedData.is_active : true
      };

      // Clear editing function (this will create a new function)
      setEditingFunction(null);
      setFunctionData(mappedData);
      setShowEditor(true);
      setMessage(`Function data pasted from clipboard! Review and save to create a new function.`);
      
      setTimeout(() => {
        setMessage('');
      }, 5000);
    } catch (err) {
      setPasteError('Failed to paste function: ' + err.message);
    }
  };

  const loadAgents = async () => {
    try {
      const response = await agents.list();
      // The agents endpoint returns a list directly, not nested in 'agents'
      setAgentsList(response.data || []);
    } catch (err) {
      console.error('Failed to load agents:', err);
    }
  };

  const loadPhoneNumbers = async () => {
    try {
      const response = await phoneNumbers.list();
      setPhoneNumbersList(response.data || []);
    } catch (err) {
      console.error('Failed to load phone numbers:', err);
    }
  };

  const getAgentPhoneNumbers = (agentId) => {
    return phoneNumbersList
      .filter(phone => phone.agent_id === agentId)
      .map(phone => phone.e164)
      .join(', ');
  };

  const loadFunctions = async () => {
    try {
      setLoading(true);
      const response = await customFunctions.list();
      setFunctions(response.data.functions || []);
    } catch (err) {
      setError('Failed to load functions: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const saveFunction = async () => {
    try {
      setError('');
      setMessage('');

      // Validate
      if (!functionData.name || !functionData.webhook_url) {
        setError('Name and webhook URL are required');
        return;
      }

      // Validate JSON
      try {
        JSON.parse(functionData.parameters);
      } catch (e) {
        setError('Parameters must be valid JSON');
        return;
      }

      if (editingFunction) {
        await customFunctions.update(editingFunction.id, functionData);
      } else {
        await customFunctions.create(functionData);
      }

      await loadFunctions(); // Reload the list

      setMessage(`Function ${editingFunction ? 'updated' : 'created'} successfully!`);
      setShowEditor(false);
      setEditingFunction(null);
      resetForm();

    } catch (err) {
      setError('Failed to save function: ' + err.message);
    }
  };

  const deleteFunction = async (id) => {
    setDeleteModal({ open: false, functionId: null, functionName: '' });
    try {
      await customFunctions.delete(id);
      await loadFunctions(); // Reload the list
      setMessage('Function deleted successfully!');
    } catch (err) {
      setError('Failed to delete function: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteClick = (func) => {
    setDeleteModal({ open: true, functionId: func.id, functionName: func.name });
  };

  const editFunction = (func) => {
    setEditingFunction(func);
    
    // Map all database fields to form fields with extensive debugging
    const mappedData = {
      name: func.name || '',
      description: func.description || '',
      trigger_event: func.trigger_event || 'call_started',
      target_agent_id: func.target_agent_id || '',
      webhook_url: func.webhook_url || '',
      http_method: func.http_method || 'POST',
      parameters: func.parameters_schema || func.parameters || '{}',
      system_prompt_modification: func.system_prompt_modification || '',
      greeting_modification: func.greeting_modification || '',
      timeout_ms: func.timeout_ms || 5000,
      max_retries: func.max_retries || 2,
      auth_header: func.auth_header || '',
      is_active: func.is_active !== undefined ? func.is_active : true
    };
    
    setFunctionData(mappedData);
    setShowEditor(true);
  };

  const togglePromptExpansion = (funcId) => {
    setExpandedPrompts(prev => ({
      ...prev,
      [funcId]: !prev[funcId]
    }));
  };

  const resetForm = () => {
    setEditingFunction(null);
    setFunctionData({
      name: '',
      description: '',
      trigger_event: 'call_started',
      target_agent_id: '',
      webhook_url: '',
      http_method: 'POST',
      parameters: '{}',
      system_prompt_modification: '',
      greeting_modification: '',
      is_active: true
    });
  };

  const testFunction = async (func) => {
    try {
      setMessage('');
      setError('');

      const testPayload = JSON.parse(func.parameters || '{}');
      const response = await customFunctions.test(func.id, testPayload);
      
      setMessage(`Function test successful! Status: ${response.data.status_code}, Response: ${JSON.stringify(response.data.response, null, 2)}`);
    } catch (err) {
      setError(`Function test failed: ${err.response?.data?.detail || err.message}`);
    }
  };

  // Filter functions based on selected agent and channel
  const filteredFunctions = functions.filter(func => {
    // Channel filter
    if (channelFilter) {
      const funcChannel = getChannelForEvent(func.trigger_event);
      if (funcChannel !== channelFilter) return false;
    }

    if (!agentFilter) return true;

    // Handle "all" option - functions with no specific target
    if (agentFilter === 'all') {
      return !func.target_agent_id || func.target_agent_id === '';
    }

    // Handle specific agent filter - compare both as strings to avoid type mismatch
    const funcAgentId = func.target_agent_id ? String(func.target_agent_id) : '';
    const filterAgentId = String(agentFilter);
    return funcAgentId === filterAgentId;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredFunctions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedFunctions = filteredFunctions.slice(startIndex, endIndex);

  // Reset to first page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [agentFilter, channelFilter]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" text="Loading functions..." />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <div>
            <h1 className="text-2xl font-bold text-foreground mb-1">Custom Functions</h1>
            <p className="text-sm text-muted-foreground">Modify agent behavior dynamically with custom webhook functions</p>
          </div>
        <button
          onClick={() => {
            resetForm();
            setShowEditor(true);
          }}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-button text-primary-foreground rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 btn-hover-gradient"
        >
            <Plus className="w-4 h-4" />
            Create Function
        </button>
        </div>
      </div>

      {message && (
        <div className="mb-4 p-3 bg-success/10 border border-success/20 rounded-lg flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-success" />
          <p className="text-sm text-success">{message}</p>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-destructive" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      {/* Two Column Layout */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-4 min-h-0 overflow-hidden">
        {/* Left Column - Functions List */}
        <div className="flex flex-col min-h-0 overflow-hidden animate-slide-in-left">
          <div className="flex-1 overflow-y-auto min-h-0 pr-2"
            style={{ 
              scrollBehavior: 'smooth',
              maxHeight: 'calc(100vh - 200px)'
            }}
          >
          {/* Filter Section */}
          <div className="mb-4 rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-4 shadow-elegant">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="block text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1.5">
                  <Filter className="w-3 h-3" />
                  Filter by Channel
                </label>
                <AnimatedDropdown
                  value={channelFilter}
                  onChange={(value) => setChannelFilter(value)}
                  options={[
                    { value: '', label: 'All Channels' },
                    { value: 'call', label: 'Phone Call Functions' },
                    { value: 'chat', label: 'Embed Chat Functions' },
                  ]}
                  placeholder="All Channels"
                  icon={Filter}
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1.5">
                  <Filter className="w-3 h-3" />
                  Filter by Agent
                </label>
                <AnimatedDropdown
                  value={agentFilter}
                  onChange={(value) => setAgentFilter(value)}
                  options={[
                    { value: '', label: 'All Functions' },
                    { value: 'all', label: 'All Agents (No specific target)' },
                    ...agentsList.map(agent => ({ value: agent.id, label: agent.name }))
                  ]}
                  placeholder="All Functions"
                  icon={Filter}
                />
              </div>
            </div>
          </div>

          {/* Functions List */}
          {functions.length === 0 ? (
            <div className="text-center py-12 rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm">
              <Code className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-foreground text-lg mb-2">No custom functions created yet</p>
              <p className="text-muted-foreground text-sm">Create functions to modify agent behavior dynamically</p>
            </div>
          ) : (
            <div className="space-y-3">
              {paginatedFunctions.length === 0 ? (
                <div className="text-center py-12 rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm">
                  <Filter className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-foreground text-lg mb-2">No functions found</p>
                  <p className="text-muted-foreground text-sm">Try adjusting your filter</p>
                </div>
              ) : (
                paginatedFunctions.map((func) => (
                <div
                  key={func.id}
                  className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-4 shadow-elegant hover:bg-card hover:border-primary/20 transition-all"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                          <Code className="w-4 h-4 text-primary-foreground" />
                        </div>
                        <h3 className="text-base font-semibold text-foreground truncate">{func.name}</h3>
                      </div>
                      <p className="text-xs text-muted-foreground mb-1 line-clamp-1">{func.description}</p>
                      {func.target_agent_id && (
                        <div className="flex items-center gap-1 mt-1">
                          <Target className="w-3 h-3 text-primary" />
                          <span className="text-xs text-muted-foreground">
                            Target: <span className="text-foreground font-medium">{agentsList.find(a => a.id === func.target_agent_id)?.name || 'Unknown Agent'}</span>
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0 ml-2 flex-wrap justify-end">
                      <span className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs border ${
                        func.is_active 
                          ? 'bg-success/20 text-success border-success/30' 
                          : 'bg-muted text-muted-foreground border-border/40'
                      }`}>
                        {func.is_active ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                        {func.is_active ? 'Active' : 'Inactive'}
                      </span>
                      {getChannelForEvent(func.trigger_event) === 'chat' ? (
                        <span className="flex items-center gap-1 px-2 py-1 rounded-md text-xs border bg-blue-500/20 text-blue-400 border-blue-500/30">
                          <MessageSquare className="w-3 h-3" />
                          Chat
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 px-2 py-1 rounded-md text-xs border bg-orange-500/20 text-orange-400 border-orange-500/30">
                          <Phone className="w-3 h-3" />
                          Call
                        </span>
                      )}
                      <span className="flex items-center gap-1 px-2 py-1 rounded-md text-xs border bg-primary/20 text-primary border-primary/30">
                        <Zap className="w-3 h-3" />
                        {func.trigger_event}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div className="p-2 rounded-lg bg-background/30 border border-border/40">
                      <p className="text-xs text-muted-foreground mb-0.5 flex items-center gap-1">
                        <LinkIcon className="w-3 h-3" />
                        Webhook URL
                      </p>
                      <p className="text-xs text-foreground break-all font-mono truncate" title={func.webhook_url}>{func.webhook_url}</p>
                    </div>
                    <div className="p-2 rounded-lg bg-background/30 border border-border/40">
                      <p className="text-xs text-muted-foreground mb-0.5">HTTP Method</p>
                      <p className="text-xs text-foreground font-semibold">{func.http_method}</p>
                    </div>
                  </div>

                  {func.system_prompt_modification && (
                    <div className="mb-2">
                      <button
                        onClick={() => togglePromptExpansion(func.id)}
                        className="w-full flex items-center justify-between p-2 rounded-lg bg-background/30 border border-border/40 hover:bg-background/40 transition-colors"
                      >
                        <div className="flex items-center gap-1.5">
                          <Zap className="w-3 h-3 text-primary" />
                          <span className="text-xs text-muted-foreground">System Prompt Modification</span>
                        </div>
                        {expandedPrompts[func.id] ? (
                          <ChevronUp className="w-3 h-3 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-3 h-3 text-muted-foreground" />
                        )}
                      </button>
                      {expandedPrompts[func.id] && (
                        <div className="mt-2 p-2 rounded-lg bg-background/20 border border-border/30">
                          <p className="text-xs text-foreground whitespace-pre-wrap">{func.system_prompt_modification}</p>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => copyFunctionAsJSON(func)}
                      className="flex items-center justify-center w-10 h-10 rounded-full border border-border/60 bg-background/50 hover:bg-background hover:border-primary/50 transition-all duration-300 text-foreground hover:shadow-lg hover:shadow-primary/10"
                      title="Copy Function as JSON"
                    >
                      <Copy className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => testFunction(func)}
                      className="flex items-center justify-center w-10 h-10 rounded-full border border-border/60 bg-background/50 hover:bg-background hover:border-primary/50 transition-all duration-300 text-foreground hover:shadow-lg hover:shadow-primary/10"
                      title="Test Function"
                    >
                      <FlaskConical className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => editFunction(func)}
                      className="flex items-center justify-center w-10 h-10 rounded-full border border-border/60 bg-background/50 hover:bg-background hover:border-primary/50 transition-all duration-300 text-foreground hover:shadow-lg hover:shadow-primary/10"
                      title="Edit Function"
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(func)}
                      className="flex items-center justify-center w-10 h-10 rounded-full border border-destructive/30 bg-destructive/10 hover:bg-destructive/20 hover:border-destructive/50 transition-all duration-300 text-destructive hover:shadow-lg hover:shadow-destructive/10 btn-hover-destructive"
                      title="Delete Function"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                ))
              )}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex justify-center items-center gap-4">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage <= 1}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-button text-primary-foreground rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed btn-hover-gradient"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              
              <div className="flex gap-2">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-2 rounded-lg transition-all ${
                        currentPage === pageNum
                          ? 'bg-gradient-button text-primary-foreground shadow-lg shadow-primary/20'
                          : 'bg-background/50 border border-border/60 text-foreground hover:bg-background hover:border-primary/20'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage >= totalPages}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-button text-primary-foreground rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed btn-hover-gradient"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Pagination Info */}
          {filteredFunctions.length > 0 && (
            <div className="mt-3 text-center text-sm text-muted-foreground">
              Showing {startIndex + 1}-{Math.min(endIndex, filteredFunctions.length)} of {filteredFunctions.length} function{filteredFunctions.length !== 1 ? 's' : ''}
            </div>
          )}
          </div>
        </div>

        {/* Right Column - Function Editor */}
      {showEditor && (
          <div className="flex flex-col min-h-0 overflow-hidden animate-slide-in-right animate-delay-100">
            <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-4 shadow-elegant mb-4 flex-shrink-0">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <Code className="w-5 h-5" />
              {editingFunction ? 'Edit Function' : 'Create Function'}
            </h3>
                <div className="flex items-center gap-2">
                  {!editingFunction && (
                    <button
                      onClick={pasteFunctionFromJSON}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border/60 bg-background/50 hover:bg-background hover:border-primary/20 transition-all duration-300 text-foreground text-sm"
                      title="Paste Function from JSON (from clipboard)"
                    >
                      <Clipboard className="w-4 h-4" />
                      Paste JSON
                    </button>
                  )}
                <button
                  onClick={() => {
                    setShowEditor(false);
                    setEditingFunction(null);
                    resetForm();
                  }}
                  className="p-1.5 rounded-lg hover:bg-background/50 transition-colors text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              </div>
              {pasteError && (
                <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-destructive" />
                  <p className="text-destructive text-sm">{pasteError}</p>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-4 shadow-elegant flex-1 overflow-y-auto min-h-0"
              style={{ 
                scrollBehavior: 'smooth',
                maxHeight: 'calc(100vh - 280px)'
              }}
            >
              <div className="space-y-4">
                {/* Basic Info */}
                <div className="space-y-3">
                <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                    Function Name
                  </label>
                  <input
                    type="text"
                    value={functionData.name}
                    onChange={(e) => setFunctionData({ ...functionData, name: e.target.value })}
                    placeholder="e.g., CRM Customer Lookup"
                      className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  />
                </div>

                <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                    Description
                  </label>
                  <textarea
                    value={functionData.description}
                    onChange={(e) => setFunctionData({ ...functionData, description: e.target.value })}
                    placeholder="What does this function do?"
                      rows={2}
                      className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none"
                  />
                </div>

                <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                    Trigger Event
                  </label>
                  <AnimatedDropdown
                    value={functionData.trigger_event}
                    onChange={(value) => setFunctionData({ ...functionData, trigger_event: value })}
                    options={triggerEvents}
                    placeholder="Select trigger event"
                  />
                  {functionData.trigger_event === 'agent_tool' ? (
                    <div className="mt-2 flex items-start gap-2 p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                      <Zap className="w-3 h-3 text-emerald-400 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-emerald-400">
                        <span className="font-semibold">Agent Tool</span> — the agent decides when to call this and <span className="font-semibold">waits</span> for the result before it speaks, so it can never announce data it does not have yet. Write the <span className="font-semibold">Description</span> for the AI: say what the function returns and when to call it. In Parameters, set a value to <code className="bg-emerald-500/20 px-1 rounded">""</code> for anything the AI should supply (e.g. <code className="bg-emerald-500/20 px-1 rounded">"timezone": ""</code>); <code className="bg-emerald-500/20 px-1 rounded">{'{{session_id}}'}</code> style values are filled in automatically.
                      </p>
                    </div>
                  ) : getChannelForEvent(functionData.trigger_event) === 'chat' ? (
                    <div className="mt-2 flex items-start gap-2 p-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
                      <MessageSquare className="w-3 h-3 text-blue-400 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-blue-400">
                        <span className="font-semibold">Embed Chat Function</span> — fired during chat sessions. Webhook response can inject <code className="bg-blue-500/20 px-1 rounded">system_prompt_addition</code>, <code className="bg-blue-500/20 px-1 rounded">context_message</code>, or <code className="bg-blue-500/20 px-1 rounded">greeting_addition</code> (Chat Form Submitted only).
                      </p>
                    </div>
                  ) : (
                    <div className="mt-2 flex items-start gap-2 p-2 rounded-lg bg-orange-500/10 border border-orange-500/20">
                      <Phone className="w-3 h-3 text-orange-400 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-orange-400">
                        <span className="font-semibold">Phone Call Function</span> — fired during voice calls. Webhook response can update the agent's system prompt or greeting in real time.
                      </p>
                    </div>
                  )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5 flex items-center gap-1.5">
                      <Target className="w-3 h-3" />
                    Target Agent
                  </label>
                  <AnimatedDropdown
                    value={functionData.target_agent_id}
                    onChange={(value) => setFunctionData({ ...functionData, target_agent_id: value })}
                    options={[
                      { value: '', label: 'All Agents' },
                      ...agentsList.map(agent => {
                        const agentPhones = getAgentPhoneNumbers(agent.id);
                        return { value: agent.id, label: `${agent.name} (${agentPhones || 'No phone numbers'})` };
                      })
                    ]}
                    placeholder="All Agents"
                    icon={Target}
                  />
                    <p className="text-xs text-muted-foreground mt-1">
                    Select specific agent or leave blank to apply to all agents
                  </p>
                </div>

                <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5 flex items-center gap-1.5">
                      <LinkIcon className="w-3 h-3" />
                    Webhook URL
                  </label>
                  <input
                    type="url"
                    value={functionData.webhook_url}
                    onChange={(e) => setFunctionData({ ...functionData, webhook_url: e.target.value })}
                    placeholder="https://integration.braincx.ai/webhook/crm-lookup"
                      className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  />
                </div>

                <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                    HTTP Method
                  </label>
                  <AnimatedDropdown
                    value={functionData.http_method}
                    onChange={(value) => setFunctionData({ ...functionData, http_method: value })}
                    options={[
                      { value: 'POST', label: 'POST' },
                      { value: 'GET', label: 'GET' },
                      { value: 'PUT', label: 'PUT' },
                      { value: 'PATCH', label: 'PATCH' },
                      { value: 'DELETE', label: 'DELETE' }
                    ]}
                    placeholder="Select HTTP method"
                  />
                </div>
              </div>

                {/* Advanced Config */}
                <div className="space-y-3 pt-3 border-t border-border/40">
                <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5 flex items-center gap-1.5">
                      <Settings className="w-3 h-3" />
                    Parameters (JSON)
                  </label>
                  <textarea
                    value={functionData.parameters}
                    onChange={(e) => setFunctionData({ ...functionData, parameters: e.target.value })}
                    placeholder='{"lookup_type": "phone", "include_history": true}'
                      rows={3}
                      className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all font-mono"
                  />
                </div>

                <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5 flex items-center gap-1.5">
                      <Zap className="w-3 h-3" />
                    System Prompt {getChannelForEvent(functionData.trigger_event) === 'chat' ? 'Injection' : 'Modification'}
                  </label>
                  <textarea
                    value={functionData.system_prompt_modification}
                    onChange={(e) => setFunctionData({ ...functionData, system_prompt_modification: e.target.value })}
                    placeholder={
                      getChannelForEvent(functionData.trigger_event) === 'chat'
                        ? "Static text to inject into the agent's system prompt (webhook response field system_prompt_addition overrides this)"
                        : "Additional instructions to add to the agent's system prompt based on the function result"
                    }
                    rows={4}
                      className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  />
                  {getChannelForEvent(functionData.trigger_event) === 'chat' && (
                    <p className="text-xs text-muted-foreground mt-1">Webhook response field <code className="bg-muted px-1 rounded">system_prompt_addition</code> takes precedence over this static value.</p>
                  )}
                </div>

                {getChannelForEvent(functionData.trigger_event) === 'call' && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      Greeting Modification
                    </label>
                    <textarea
                      value={functionData.greeting_modification}
                      onChange={(e) => setFunctionData({ ...functionData, greeting_modification: e.target.value })}
                      placeholder="How should the agent modify its greeting based on the function result?"
                      rows={2}
                      className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none"
                    />
                  </div>
                )}
                {getChannelForEvent(functionData.trigger_event) === 'chat' && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5 flex items-center gap-1.5">
                      <MessageSquare className="w-3 h-3 text-blue-400" />
                      Context Message Injection
                    </label>
                    <textarea
                      value={functionData.greeting_modification}
                      onChange={(e) => setFunctionData({ ...functionData, greeting_modification: e.target.value })}
                      placeholder="Optional: static context prefix to prepend to user messages when this function fires (webhook response overrides this)"
                      rows={2}
                      className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Webhook response field <code className="bg-muted px-1 rounded">context_message</code> takes precedence over this static value.</p>
                  </div>
                )}

                  <div className="flex items-center gap-2 p-3 rounded-lg bg-background/30 border border-border/40">
                    {functionData.is_active ? (
                      <ToggleRight className="w-5 h-5 text-success cursor-pointer" onClick={() => setFunctionData({ ...functionData, is_active: false })} />
                    ) : (
                      <ToggleLeft className="w-5 h-5 text-muted-foreground cursor-pointer" onClick={() => setFunctionData({ ...functionData, is_active: true })} />
                    )}
                    <span className="text-sm text-foreground font-medium">Function is active</span>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-3 border-t border-border/40">
                  <button
                    onClick={() => {
                      setShowEditor(false);
                      setEditingFunction(null);
                      resetForm();
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border/60 bg-background/50 hover:bg-background hover:border-primary/20 transition-all duration-300 text-foreground text-sm"
                  >
                    <X className="w-3 h-3" />
                    Cancel
                  </button>
                  <button
                    onClick={saveFunction}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-button text-primary-foreground rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 text-sm btn-hover-gradient"
                  >
                    <Save className="w-3 h-3" />
                    {editingFunction ? 'Update' : 'Create'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.open && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 w-full max-w-md mx-4 shadow-elegant">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-destructive/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-destructive" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">
                Delete this function?
              </h3>
            </div>
            
            <p className="text-muted-foreground text-sm mb-2">
              Are you sure you want to delete <span className="text-foreground font-medium">"{deleteModal.functionName}"</span>?
            </p>
            <p className="text-muted-foreground text-xs mb-6">
              This action cannot be undone.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteModal({ open: false, functionId: null, functionName: '' })}
                className="px-4 py-2 bg-muted/50 hover:bg-muted border border-border/60 text-foreground rounded-lg transition-all duration-300 text-sm font-medium btn-hover-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteFunction(deleteModal.functionId)}
                className="px-4 py-2 bg-destructive hover:bg-destructive/90 text-destructive-foreground rounded-lg transition-all duration-300 text-sm font-medium btn-hover-destructive"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomFunctions;
